import { writable, derived, get } from 'svelte/store';
import { selectedParticipant } from './selectionStore';
import { isAuthenticated } from '../lib/auth.js';

// Store for participant message counts by participant
const participantMessageCounts = writable({});

// Store for tracking when counts were last fetched
const lastFetchTimes = writable({});

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// API base URL
const API_BASE_URL = '/api/proxy';

/**
 * Fetch unread message count for a specific participant
 * @param {string} participantId - The participant ID
 * @returns {Promise<number>} - The count of unread messages
 */
async function fetchUnreadParticipantMessageCount(participantId) {
  try {
    // Fetch only unread messages with minimal page size to get count
    const url = `${API_BASE_URL}/messages?page=1&pageSize=1&subjectGUID=${participantId}&status=Sent`;
    
    const res = await fetch(url);
    
    if (!res.ok) {
      throw new Error('Failed to fetch participant message count');
    }
    
    const responseData = await res.json();
    return responseData.totalCount || 0;
  } catch (error) {
    console.error('Error fetching unread participant message count:', error);
    return 0;
  }
}

/**
 * Check if cache is still valid
 * @param {string} participantId - The participant ID
 * @returns {boolean} - Whether cache is still valid
 */
function isCacheValid(participantId) {
  const lastFetch = $lastFetchTimes[participantId];
  if (!lastFetch) return false;
  
  return (Date.now() - lastFetch) < CACHE_DURATION;
}

/**
 * Get message count for current participant with caching. Read-side cache
 * miss also triggers a fetch — gated on isAuthenticated so we don't 401
 * during session boot.
 */
export const currentParticipantMessageCount = derived(
  [selectedParticipant, participantMessageCounts, lastFetchTimes, isAuthenticated],
  ([$selectedParticipant, $participantMessageCounts, $lastFetchTimes, $isAuthenticated]) => {
    if (!$selectedParticipant) return 0;

    const participantId = $selectedParticipant._id;
    const lastFetch = $lastFetchTimes[participantId];
    const currentCount = $participantMessageCounts[participantId] || 0;

    // Return cached value if available and valid
    if ($participantMessageCounts[participantId] !== undefined && lastFetch && (Date.now() - lastFetch) < CACHE_DURATION) {
      return currentCount;
    }

    // Trigger fetch if cache is invalid AND auth is ready (skip during boot)
    if ($isAuthenticated && (!lastFetch || (Date.now() - lastFetch) >= CACHE_DURATION)) {
      fetchAndUpdateParticipantCount(participantId);
    }

    return currentCount;
  }
);

/**
 * Fetch and update message count for a participant
 * @param {string} participantId - The participant ID
 */
export async function fetchAndUpdateParticipantCount(participantId) {
  if (!participantId) return;
  
  const count = await fetchUnreadParticipantMessageCount(participantId);
  
  participantMessageCounts.update(counts => ({
    ...counts,
    [participantId]: count
  }));
  
  lastFetchTimes.update(times => ({
    ...times,
    [participantId]: Date.now()
  }));
}

/**
 * Force refresh message count for current participant
 */
export async function refreshCurrentParticipantCount() {
  // Get the current selected participant from the store
  const selectedParticipantValue = get(selectedParticipant);
  if (selectedParticipantValue) {
    await fetchAndUpdateParticipantCount(selectedParticipantValue._id);
  }
}

/**
 * Force refresh message count for a specific participant
 * @param {string} participantId - The participant ID to refresh count for
 */
export async function forceRefreshParticipantCount(participantId) {
  if (participantId) {
    // Clear the cache for this participant to force a fresh fetch
    clearParticipantCache(participantId);
    // Fetch and update the count
    const count = await fetchUnreadParticipantMessageCount(participantId);
    
    // Update the store directly
    participantMessageCounts.update(counts => ({
      ...counts,
      [participantId]: count
    }));
    
    lastFetchTimes.update(times => ({
      ...times,
      [participantId]: Date.now()
    }));
  }
}

/**
 * Clear cache for a specific participant
 * @param {string} participantId - The participant ID
 */
export function clearParticipantCache(participantId) {
  participantMessageCounts.update(counts => {
    const newCounts = { ...counts };
    delete newCounts[participantId];
    return newCounts;
  });
  
  lastFetchTimes.update(times => {
    const newTimes = { ...times };
    delete newTimes[participantId];
    return newTimes;
  });
}

/**
 * Clear all caches
 */
export function clearAllParticipantCaches() {
  participantMessageCounts.set({});
  lastFetchTimes.set({});
}

// Auto-fetch when EITHER the participant changes OR auth becomes ready.
// Without gating on isAuthenticated, the first subscribe fires during session
// boot before the Auth0 SDK has a token — installAuthFetch then sends the
// call without a Bearer header, the backend returns 401, and the badge stays
// at 0 until the 5-minute auto-refresh catches up. Mirror of the fix in
// stores/messageCountStore.js — same root cause.
derived([selectedParticipant, isAuthenticated], ([$p, $authed]) => ({ $p, $authed }))
  .subscribe(({ $p, $authed }) => {
    if ($p?._id && $authed) {
      fetchAndUpdateParticipantCount($p._id);
    }
  });

// Auto-refresh every 5 minutes (skip when not authenticated to avoid the
// same tokenless-fetch 401).
if (typeof window !== 'undefined') {
  setInterval(() => {
    const selectedParticipantValue = get(selectedParticipant);
    const authed = get(isAuthenticated);
    if (selectedParticipantValue?._id && authed) {
      fetchAndUpdateParticipantCount(selectedParticipantValue._id);
    }
  }, CACHE_DURATION);
} 